import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { MY_SUBJECTS, MY_CGPA, ANNOUNCEMENTS, SOCIAL_FEED, CREATE_SOCIAL_POST, LIKE_SOCIAL_POST } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFeatures, ROUTE_DOMAINS } from '../context/FeatureContext';
import {
  BookOpen, Briefcase, Users, Wallet, Star, Home,
  CalendarCheck, GraduationCap, Activity,
  Megaphone, ArrowRight, ChevronRight,
  Heart, MessageCircle, Send,
} from 'lucide-react';

/* ── Zone quick-access grid ── */
const ZONES = [
  {
    label: 'Study Hub', gradient: '#1a73e8',
    lightBg: '#EFF6FF', lightBorder: '#BFDBFE', icon: BookOpen,
    links: [
      { to: '/notes',      label: 'Notes'      },
      { to: '/materials',  label: 'Materials'  },
      { to: '/playlists',  label: 'Playlists'  },
      { to: '/flashcards', label: 'Flashcards' },
    ],
  },
  {
    label: 'Career', gradient: '#1257a0',
    lightBg: '#FAF6EE', lightBorder: '#E8D9BE', icon: Briefcase,
    links: [
      { to: '/opportunities', label: 'Opportunities' },
      { to: '/scholarships',  label: 'Scholarships'  },
      { to: '/resume',        label: 'Resume'        },
      { to: '/linkedin',      label: 'LinkedIn'      },
    ],
  },
  {
    label: 'Connect', gradient: '#188038',
    lightBg: '#F0F5EF', lightBorder: '#BDD8BA', icon: Users,
    links: [
      { to: '/social',    label: 'Student Social'  },
      { to: '/network',   label: 'Network & Groups' },
      { to: '/forums',    label: 'Forums'          },
      { to: '/knowledge', label: 'Knowledge Loop'  },
    ],
  },
  {
    label: 'Campus', gradient: '#b06000',
    lightBg: '#FAF7F1', lightBorder: '#E5D8C0', icon: Home,
    links: [
      { to: '/food',        label: 'Food Share'  },
      { to: '/cab',         label: 'Cab Share'   },
      { to: '/pg-finder',   label: 'PG Finder'   },
      { to: '/marketplace', label: 'Marketplace' },
    ],
  },
  {
    label: 'Money & Docs', gradient: '#188038',
    lightBg: '#EEF4EC', lightBorder: '#BAD5B6', icon: Wallet,
    links: [
      { to: '/expenses',   label: 'Expenses'   },
      { to: '/documents',  label: 'Vault'      },
      { to: '/grievances', label: 'Grievances' },
      { to: '/projects',   label: 'Projects'   },
    ],
  },
  {
    label: 'Explore', gradient: '#1a73e8',
    lightBg: '#F3F0FA', lightBorder: '#D4C8F0', icon: Star,
    links: [
      { to: '/campus-news', label: 'Campus News' },
      { to: '/hackathons',  label: 'Hackathons'  },
      { to: '/showcase',    label: 'Showcase'    },
      { to: '/sports',      label: 'Sports'      },
    ],
  },
];

/* ── New feature chips ── */
const NEW_FEATURES = [
  { label: 'Student Social',  sub: 'Share & discover on campus', to: '/social',   grad: '#1257a0',  light: '#FFF7ED' },
  { label: 'Study Materials', sub: 'Thousands of resources',  to: '/materials', grad: '#1a73e8',  light: '#EFF6FF' },
  { label: 'Curated Playlists', sub: 'YouTube learning paths', to: '/playlists', grad: '#c62828',  light: '#FEF2F2' },
  { label: 'PG Finder',       sub: 'Find roommates near campus', to: '/pg-finder',grad: '#b06000', light: '#FFFBEB' },
  { label: 'Forums',          sub: 'Ask, learn, connect',     to: '/forums',      grad: '#188038',  light: '#ECFDF5' },
  { label: 'Marketplace',     sub: 'Buy, sell or donate',     to: '/marketplace', grad: '#1a73e8',  light: '#FAF5FF' },
];

/* ── Greeting banner ── */
function GreetingBanner({ user }) {
  const { isOff } = useFeatures();
  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="relative overflow-hidden rounded-2xl mb-6" style={{ background: '#1257a0' }}>
      <div className="absolute bottom-[-30%] left-[10%] w-48 h-48 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle,#C084FC 0%,transparent 70%)', filter: 'blur(40px)' }} />

      <div className="relative z-10 p-6 md:p-8">
        <p className="text-white/70 text-sm mb-1">{greeting}</p>
        <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">
          {user?.name?.split(' ')[0] || 'Student'}
        </h1>
        <p className="text-white/50 text-sm">
          {user?.college || 'Aura'} · {user?.branch || 'Engineering'}
        </p>
        <div className="flex gap-2 mt-5 flex-wrap">
          <Link to="/social"
            className="px-4 py-2 rounded-xl text-sm font-semibold text-brand-700 bg-white hover:bg-gray-50 transition-all">
            Student Social
          </Link>
          {!isOff('Campus News') && (
            <Link to="/campus-news"
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white border border-white/25 hover:bg-white/10 transition-all backdrop-blur-sm">
              Campus News
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Social Pulse — live Student Social module, the dashboard's lead content ── */
function SocialPulse({ user }) {
  const [text, setText] = useState('');
  const { data, refetch } = useQuery(SOCIAL_FEED, { fetchPolicy: 'cache-and-network' });
  const [create, { loading: posting }] = useMutation(CREATE_SOCIAL_POST, {
    onCompleted: () => { setText(''); refetch(); },
  });
  const [like] = useMutation(LIKE_SOCIAL_POST, { onCompleted: () => refetch() });

  const posts = (data?.socialFeed || []).slice(0, 3);

  const share = (e) => {
    e.preventDefault();
    if (!text.trim() || posting) return;
    create({ variables: { postType: 'THOUGHT', title: text.trim(), tags: ['campus-life'] } });
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold text-gray-400 dark:text-white/30 uppercase tracking-widest flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ background: '#1a73e8' }} />
          Student Social
        </h2>
        <Link to="/social"
          className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-500 flex items-center gap-1 font-medium transition-colors">
          Open feed <ArrowRight size={11} />
        </Link>
      </div>

      {/* Quick composer */}
      <form onSubmit={share}
        className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100 dark:border-white/[0.06] bg-white dark:bg-surface-800 mb-3">
        <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
          style={{ background: '#1a73e8' }}>
          {user?.name?.[0]?.toUpperCase() || 'S'}
        </span>
        <input
          className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 focus:outline-none"
          placeholder="Share an update with your campus…"
          value={text}
          onChange={e => setText(e.target.value)}
          maxLength={180}
        />
        <button type="submit" disabled={!text.trim() || posting}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all active:scale-95 disabled:opacity-40 flex-shrink-0"
          style={{ background: '#1a73e8' }}>
          {posting
            ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            : <Send size={15} />}
        </button>
      </form>

      {/* Latest posts */}
      {posts.length === 0 ? (
        <Link to="/social"
          className="block p-4 rounded-2xl border border-dashed border-gray-200 dark:border-white/[0.1] text-center text-sm text-gray-400 dark:text-white/35 hover:border-brand-400 hover:text-brand-500 transition-colors">
          No posts yet. Be the first to share an update.
        </Link>
      ) : (
        <div className="space-y-2">
          {posts.map(p => (
            <div key={p.id}
              className="flex items-start gap-3 p-4 rounded-2xl border border-gray-100 dark:border-white/[0.06] bg-white dark:bg-surface-800 hover:shadow-sm dark:hover:shadow-none transition-all">
              <span className="w-9 h-9 rounded-full bg-brand-50 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                <MessageCircle size={15} className="text-brand-600" />
              </span>
              <div className="min-w-0 flex-1">
                <Link to="/social" className="block">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">{p.title}</p>
                  {p.body && <p className="text-xs text-gray-400 dark:text-white/35 mt-0.5 line-clamp-1">{p.body}</p>}
                  <p className="text-[11px] text-gray-300 dark:text-white/25 mt-1">
                    {p.author?.name}{p.author?.college ? ` · ${p.author.college}` : ''}
                  </p>
                </Link>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 mt-0.5">
                <button onClick={() => like({ variables: { postId: p.id } })}
                  className={`flex items-center gap-1 text-xs transition-colors ${p.hasLiked ? 'text-pink-500' : 'text-gray-300 dark:text-white/25 hover:text-pink-400'}`}>
                  <Heart size={13} fill={p.hasLiked ? 'currentColor' : 'none'} /> {p.likes}
                </button>
                <Link to="/social" className="flex items-center gap-1 text-xs text-gray-300 dark:text-white/25 hover:text-brand-400 transition-colors">
                  <MessageCircle size={13} /> {p.comments?.length || 0}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── My Trackers — manual-entry academic tools, kept out of the top banner ── */
function TrackerSection({ cgpa, subjects, showQuickStats }) {
  if (!showQuickStats) return null;

  const total    = subjects?.reduce((s, sub) => s + (sub.totalClasses    || 0), 0) || 0;
  const attended = subjects?.reduce((s, sub) => s + (sub.attendedClasses || 0), 0) || 0;
  const pct      = total > 0 ? Math.round((attended / total) * 100) : 0;

  const stats = [
    {
      label: 'CGPA', value: cgpa?.overallCgpa || '—', sub: 'Overall score', to: '/cgpa',
      gradient: '#1a73e8',
      lightBg: '#EFF6FF', icon: GraduationCap,
    },
    {
      label: 'Attendance', value: `${pct}%`, sub: total > 0 ? `${attended}/${total} classes` : 'Log your classes', to: '/attendance',
      gradient: '#188038',
      lightBg: '#ECFDF5', icon: CalendarCheck,
      bar: pct,
    },
    {
      label: 'Subjects', value: subjects?.length || '—', sub: 'This semester', to: '/timetable',
      gradient: '#1257a0',
      lightBg: '#FFF7ED', icon: BookOpen,
    },
    {
      label: 'Exams', value: 'Plan', sub: 'Schedule & results', to: '/exams',
      gradient: '#1a73e8',
      lightBg: '#FAF5FF', icon: Activity,
    },
  ];

  return (
    <div className="mb-6">
      <h2 className="text-xs font-bold text-gray-400 dark:text-white/30 uppercase tracking-widest mb-1 flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm" style={{ background: '#1a73e8' }} />
        My Trackers
      </h2>
      <p className="text-xs text-gray-400 dark:text-white/30 mb-3">Self-maintained tools — tap a card to add or update your data.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map(s => {
        const Icon = s.icon;
        return (
          <Link to={s.to} key={s.label}
            className="relative overflow-hidden rounded-2xl p-4 border border-gray-100 dark:border-white/[0.06] bg-white dark:bg-surface-800 transition-all hover:shadow-md dark:hover:shadow-none hover:-translate-y-0.5">
            <div className="flex items-start justify-between mb-3">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: s.gradient }}>
                <Icon size={16} className="text-white" />
              </span>
              <span className="text-xs text-gray-400 dark:text-white/30 font-medium">{s.label}</span>
            </div>
            <p className="text-2xl font-black text-gray-900 dark:text-white leading-none">{s.value}</p>
            <p className="text-xs text-gray-400 dark:text-white/40 mt-1">{s.sub}</p>
            {s.bar !== undefined && (
              <div className="mt-3 h-1 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(s.bar, 100)}%`, background: s.gradient }} />
              </div>
            )}
          </Link>
        );
      })}
      </div>
    </div>
  );
}

/* ── New features strip ── */
function NewFeatureStrip({ show }) {
  const { isOff } = useFeatures();
  const features = NEW_FEATURES.filter(f => !isOff(ROUTE_DOMAINS[f.to]));
  if (!show || features.length === 0) return null;
  return (
    <div className="mb-6">
      <h2 className="text-xs font-bold text-gray-400 dark:text-white/30 uppercase tracking-widest mb-3 flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm" style={{ background: '#1a73e8' }} />
        Recently added
      </h2>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
        {features.map(f => (
          <Link
            key={f.to}
            to={f.to}
            className="flex-shrink-0 rounded-2xl p-4 flex flex-col gap-2.5 min-w-[148px] transition-all hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-none active:scale-95 bg-white dark:bg-surface-800 border border-gray-100 dark:border-white/[0.06]"
          >

            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{f.label}</p>
              <p className="text-xs text-gray-400 dark:text-white/35 mt-0.5">{f.sub}</p>
            </div>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full w-fit text-white"
              style={{ background: '#1a73e8' }}>
              Open
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Zone grid ── */
function ZoneGrid() {
  const { isOff } = useFeatures();
  const zones = ZONES
    .map(zone => ({ ...zone, links: zone.links.filter(link => !isOff(ROUTE_DOMAINS[link.to])) }))
    .filter(zone => zone.links.length > 0);
  return (
    <div className="mb-6">
      <h2 className="text-xs font-bold text-gray-400 dark:text-white/30 uppercase tracking-widest mb-3 flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm" style={{ background: '#1a73e8' }} />
        Quick Access
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {zones.map(zone => (
          <div key={zone.label}
            className="group relative overflow-hidden rounded-2xl p-4 dark:border-white/[0.06] dark:bg-surface-800 transition-all hover:shadow-md dark:hover:shadow-none hover:-translate-y-0.5 flex flex-col gap-3"
            style={{ background: zone.lightBg, borderColor: zone.lightBorder, borderWidth: 1, borderStyle: 'solid' }}>
            {/* Subtle gradient accent top-right */}
            <div className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{ background: `radial-gradient(circle at top right, ${zone.gradient}22 0%, transparent 70%)` }} />

            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: zone.gradient }}>
                {React.createElement(zone.icon, { size: 15, className: 'text-white' })}
              </span>
              <span className="font-bold text-gray-900 dark:text-white text-sm">{zone.label}</span>
            </div>

            <div className="space-y-0.5">
              {zone.links.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between text-xs text-gray-500 dark:text-white/40 hover:text-gray-900 dark:hover:text-white py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.05] transition-all group/link"
                >
                  <span>{link.label}</span>
                  <ChevronRight size={11} className="opacity-0 group-hover/link:opacity-60 transition-opacity -mr-0.5" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Announcements ── */
function AnnouncementFeed({ announcements }) {
  if (!announcements?.length) return null;
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold text-gray-400 dark:text-white/30 uppercase tracking-widest flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ background: '#1a73e8' }} />
          Latest Announcements
        </h2>
        <Link to="/announcements"
          className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-500 flex items-center gap-1 font-medium transition-colors">
          View all <ArrowRight size={11} />
        </Link>
      </div>
      <div className="space-y-2">
        {announcements.slice(0, 3).map((ann, i) => (
          <div key={ann.id}
            className="flex items-start gap-3 p-4 rounded-2xl border border-gray-100 dark:border-white/[0.06] bg-white dark:bg-surface-800 hover:shadow-sm dark:hover:shadow-none transition-all hover:-translate-y-0.5 group">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: '#1a73e8' }}>
              <Megaphone size={14} className="text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{ann.title}</p>
              <p className="text-xs text-gray-400 dark:text-white/35 mt-0.5 line-clamp-1">{ann.body || ann.content}</p>
            </div>
            <ChevronRight size={14} className="text-gray-300 dark:text-white/20 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Page ── */
export default function DashboardPage() {
  const { user } = useAuth();
  const { flags } = useTheme();
  const { isOff } = useFeatures();
  const { data: subjectsData }     = useQuery(MY_SUBJECTS,    { fetchPolicy: 'cache-and-network' });
  const { data: cgpaData }         = useQuery(MY_CGPA,        { fetchPolicy: 'cache-and-network' });
  const { data: announcementsData } = useQuery(ANNOUNCEMENTS, { fetchPolicy: 'cache-and-network', skip: isOff('Announcements') });

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 animate-fade-in">
      <GreetingBanner user={user} />
      <TrackerSection
        cgpa={cgpaData?.myCgpa}
        subjects={subjectsData?.mySubjects || []}
        showQuickStats={flags.showQuickStats}
      />
      <ZoneGrid />
      {!isOff('Announcements') && (
        <AnnouncementFeed announcements={announcementsData?.announcements || []} />
      )}
      <SocialPulse user={user} />
      <NewFeatureStrip show={flags.showNewFeatures} />
    </div>
  );
}
